import type { History } from 'history';

import { createHistory as createHistoryImpl } from '../../custom-ui/bridge/methods/history';
import { createBridgeResource } from '../bridge-resource';
import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';

/**
 * Tracks the active `history.listen` subscriptions created for a bridge instance so they
 * are removed when the bridge context is destroyed.
 */
const historyListenersResource = createBridgeResource<Set<() => void>>(
	'history-listeners',
	() => new Set(),
	(unlistenFns) => {
		for (const unlisten of [...unlistenFns]) {
			unlisten();
		}
		unlistenFns.clear();
	},
);

type CreateHistoryContext = MakeBridgeContext<'bridgeConfig' | 'claimBridgeResource'>;

/**
 * Returns a wrapped History object scoped to the app's extension base path.
 *
 * Happy path: `bridgeConfig.history` and `bridgeConfig.extensionBasePath` are both
 * present — the factory returns a History whose `push`, `replace`, and `listen`
 * automatically translate between the full page path and the extension-relative route.
 *
 * Error path: either field is absent — throws a `BridgeClientError` with the message
 * "History API is not available within this module." which propagates to the app.
 */
export const createHistory: TBridgeMethod<
	CreateHistoryContext,
	void,
	History | undefined
> = async ({ bridgeConfig, claimBridgeResource }) => {
	return createHistoryImpl({
		history: bridgeConfig.history,
		extensionBasePath: bridgeConfig.extensionBasePath,
		unlistenRegistry: claimBridgeResource(historyListenersResource),
	});
};
